import { useCallback, useEffect, useMemo } from "react";
import { View, Text, StyleSheet, Image, Linking, TouchableOpacity } from "react-native";
import { createStackNavigator } from "@react-navigation/stack";
import { HeaderBackButton } from "@react-navigation/elements";
import { platform, os } from "../platform";
import ParksStore from "../storage/parksStore";
import AppStore from "../storage/appStore.js";
import Parks from "../pages/Parks";
import ParksList from "../pages/ParksList";
import ParkDetails from "../pages/ParkDetails";
import { COLORS } from "../theme/colors";
import  STRINGS  from "../constants/strings.js";
import Filter from "../pages/Filter";

const ParksStack = createStackNavigator();

const DownloadButtonTemplate = ({ url, src }) => {
  return (
    <TouchableOpacity onPress={() => Linking.openURL(url)} style={{ marginRight: 16 }}>
      <Image source={src} style={{ width: 24, height: 24 }} resizeMode="contain" />
    </TouchableOpacity>
  );
};

const GoogleDownloadButton = () => {
  return (
    <DownloadButtonTemplate
      url={STRINGS.googlePlaystoreLink}
      src={require("../assets/images/google-play-icon.png")}
    />
  );
};

const AppleDownloadButton = () => {
  return (
    <DownloadButtonTemplate url={STRINGS.appleAppstoreLink} src={require("../assets/images/app-store-icon.png")} />
  );
};

const DownloadButton = () => {
  if (/Android/i.test(navigator.userAgent)) {
    return <GoogleDownloadButton />;
  }
  if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    return <AppleDownloadButton />;
  }
  if (
    platform === "web" &&
    /Android/i.test(navigator.userAgent) === false &&
    /iPhone|iPad|iPod/i.test(navigator.userAgent) === false
  ) {
    return (
      <View style={{ flexDirection: "row", alignItems: "center" }}>
        <AppleDownloadButton />
        <GoogleDownloadButton />
      </View>
    );
  }
};

const HeaderTitle = ({ title }) => (
  <View>
    <Text style={styles.headerTitle} maxFontSizeMultiplier={1.3}>
      {title}
    </Text>
  </View>
);

export default function ParksNavigator() {
  const getParks = ParksStore((state) => state.getParks);
  const { selectAllAmenities } = ParksStore();
  const pageName = AppStore((state) => state.pageName);
  const setPageName = AppStore((state) => state.setPageName);
  const loading = AppStore((state) => state.loading);
  const parks = ParksStore((state) => state.parks);
  const locationDetails = ParksStore((state) => state.locationDetails);

  const HeaderBackButtonComponent = useCallback(
    (navigation) => (
      <HeaderBackButton
        tintColor={COLORS.text}
        onPress={() => {
          navigation.navigate("ParksMap");
        }}
      />
    ),
    []
  );

  const allAmenities = useCallback(() => {
    selectAllAmenities();
  }, [selectAllAmenities]);

  useEffect(() => {
    if (pageName !== "Parks") {
      setPageName("Parks");
    }
  }, [pageName, setPageName]);

  useEffect(() => {
    try {
      getParks();
    } catch (error) {
      console.error("Error fetching parks data:", error);
    }
  }, [getParks]);

  const parksCount = `Parks • ${loading ? "Loading" : `${parks.length}`} results`;

  return (
    <View style={styles.parentContainer}>
      <ParksStack.Navigator initialRouteName="ParksMap">
        <ParksStack.Screen
          name="ParksMap"
          component={Parks}
          options={() => {
            return {
              headerLeft: () => null,
              headerShown: true,
              headerTitle: <HeaderTitle title={parksCount} />,
              title: platform === "web" ? parksCount : <HeaderTitle title={parksCount} />,
              headerRight: () => <DownloadButton />,
            };
          }}
        />
        <ParksStack.Screen
          name="ParksList"
          component={ParksList}
          options={{
            headerShown: true,
            headerTitle: <HeaderTitle title={parksCount} />,
            title: platform === "web" ? parksCount : <HeaderTitle title={parksCount} />,
            headerBackTitleVisible: false,
            headerRight: () => <DownloadButton />,
          }}
        />
        <ParksStack.Screen
          name="Filter"
          component={Filter}
          options={({ navigation }) => {
            const canGoBack = navigation.canGoBack();
            const hideNavigationBar = os === "android" && !canGoBack ? { headerShown: false } : {};

            let backButton = {};

            if (platform === "web" && !canGoBack) {
              backButton = { headerLeft: () => HeaderBackButtonComponent(navigation) };
            }
            return {
              ...hideNavigationBar,
              ...backButton,
              headerShown: true,
              headerTitle: <HeaderTitle title="Filter Parks" />,
              headerBackTitleVisible: false,
              title: platform === "web" ? "Filter Parks" : <HeaderTitle title="Filter Parks" />,
              headerRight: () => {
                return (
                  <TouchableOpacity onPress={allAmenities} style={{ marginRight: 16 }}>
                    <Text style={{ color: COLORS.secondary, fontSize: 16 }} maxFontSizeMultiplier={1.2}>
                      {"Select All"}
                    </Text>
                  </TouchableOpacity>
                );
              },
            };
          }}
        />
        <ParksStack.Screen
          name="ParkDetails"
          component={ParkDetails}
          options={({ navigation }) => {
            const canGoBack = navigation.canGoBack();
            const hideNavigationBar = os === "android" && !canGoBack ? { headerShown: false } : {};

            let backButton = {};

            if (platform === "web" && !canGoBack) {
              backButton = { headerLeft: () => HeaderBackButtonComponent(navigation) };
            }
            return {
              ...hideNavigationBar,
              ...backButton,
              headerTitle: "",
              headerBackTitleVisible: false,
              title: locationDetails?.title || "Loading details",
            };
          }}
        />
      </ParksStack.Navigator>
    </View>
  );
}

const styles = StyleSheet.create({
  parentContainer: {
    flex: 1,
  },
  headerTitle: {
    fontWeight: "bold",
    fontSize: 18,
  },
});
